import React, { useEffect, useState } from 'react'
import { useUser } from 'util/UserContext'
import { useNavigate, useParams } from 'react-router-dom'
import TeamSettingsProfile from './TeamSettingsProfile'
import TeamSettingsPrivacy from './TeamSettingsPrivacy'
import TeamSettingsMembers from './TeamSettingsMembers'
import ScrollableTabsList from 'ui/ScrollableTabsList'
import api from 'config/axiosConfig'
import { Team } from 'util/constants'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'ui/InputSelect'
import { Users } from 'lucide-react'
import { AxiosError } from 'axios'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import { apiRoutes } from "util/apiRoutes";

const TeamSettings: React.FC = () => {
    const { currentUser, awaitingUser } = useUser()
    const navigate = useNavigate()
    const { teamname, tab } = useParams<{ teamname: string; tab: string }>()
    const [selectedTab, setSelectedTab] = useState<string>(tab || 'profile')
    const [team, setTeam] = useState<Team | null>(null)
    const [moderatedTeams, setModeratedTeams] = useState<Team[]>([])
    const [error, setError] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(true)

    // Redirect if not logged in
    useEffect(() => {
        if (!awaitingUser && !currentUser) {
            navigate('/', { replace: true })
        }
    }, [currentUser, awaitingUser, navigate])

    // Fetch moderated teams
    useEffect(() => {
        const fetchModeratedTeams = async () => {
            try {
                const response = await api.get<Team[]>(apiRoutes.team.moderated)
                setModeratedTeams(response.data)
            } catch (error) {
                console.error('Error fetching moderated teams:', error)
                // Don't set error for moderated teams fetch failure - it's not critical
            }
        }

        if (currentUser) {
            fetchModeratedTeams()
        }
    }, [currentUser])

    // Fetch team data and verify permissions
    useEffect(() => {
        const fetchTeam = async () => {
            try {
                const response = await api.get<Team>(apiRoutes.team.byName(teamname || ""))
                const teamData = response.data
                
                // Verify user has permission (creator or moderator)
                if (!awaitingUser && teamData.properties_public?.creator_id !== currentUser?.id) {
                    // Check if user is a moderator of this team
                    const isModeratedTeam = moderatedTeams.some(team => team.teamname === teamname)
                    if (!isModeratedTeam) {
                        navigate(`/partea/${teamname}`)
                        return
                    }
                }
                
                setTeam(teamData)
                setLoading(false)
            } catch (error) {
                setLoading(false)
                if (error instanceof AxiosError && error.response?.status === 404) {
                    setError(`404: Partea with teamname: ${teamname} does not exist or is inactive.`)
                } else {
                    console.error(`Error fetching Partea with teamname: ${teamname}`, error)
                    setError(`Failed to fetch team data`)
                }
            }
        }

        if (teamname) {
            fetchTeam()
        }
    }, [teamname, currentUser, navigate, moderatedTeams, awaitingUser])

    // Update selectedTab when URL tab parameter changes
    useEffect(() => {
        if (tab) {
            setSelectedTab(tab)
        } else {
            setSelectedTab('profile')
        }
    }, [tab])

    const handleTeamChange = (value: string) => {
        navigate(`/partea/${value}/settings/${selectedTab}`)
    }

    if (loading) {
        return (
            <div className="max-w-4xl m-4">
                <LoadingSkeleton variant="settings" count={3} />
            </div>
        )
    }

    if (error) {
        return error.match(/404/) ? (
            <NotFoundPage 
                displayStyle="page" 
                errorMessage="Sorry, no one's using that Partea name." 
                errorSubtext="The Partea may be banned or inactive, or the name is incorrect." 
                buttonText="Look Somewhere Else" 
            />
        ) : (
            <ServerErrorPage />
        )
    }

    if (!team) {
        return <NotFoundPage displayStyle="page" errorMessage="Sorry, that Partea doesn't exist." />
    }

    const tabsData = [
        {
            label: 'Profile',
            route: `/partea/${teamname}/settings/profile`,
            value: 'profile',
        },
        {
            label: 'Privacy',
            route: `/partea/${teamname}/settings/privacy`,
            value: 'privacy',
        },
        {
            label: 'Members',
            route: `/partea/${teamname}/settings/members`,
            value: 'members',
        },
    ]

    return (
        <div className="max-w-4xl m-4">
            <h1 className="flex flex-row flex-wrap gap-x-1 my-2 items-center text-eoe-text-heading dark:text-eoe-dark-text-heading text-xl font-bold">
                {`Settings ❯`}&nbsp;
                
                <Select onValueChange={handleTeamChange} value={teamname}>
                    <SelectTrigger className="px-2 py-0 rounded-full text-base flex-shrink bg-transparent border border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover">
                        <SelectValue placeholder="Select a Partea" />
                    </SelectTrigger>
                    <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main text-base">
                        {moderatedTeams.map((moderatedTeam) => (
                            <SelectItem key={moderatedTeam.id} value={moderatedTeam.teamname}>
                                <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Users className='size-4' />
                                    <span>{moderatedTeam.teamname}</span>
                                </div>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </h1>
            
            {/* Tabs Navigation */}
            <div className="horizontal-tablist my-2">
                <ScrollableTabsList
                    key="team-settings-tabs"
                    tabs={tabsData}
                    selectedTab={selectedTab}
                    onTabChange={() => {}}
                    pageTitle={`Settings > ${teamname}`}
                />
            </div>

            {/* Tabs Content */}
            <div className="">
                {selectedTab === 'profile' && (
                    <div
                        role="tabpanel"
                        id="profile-panel"
                        aria-labelledby="profile-tab"
                        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                    >
                        <TeamSettingsProfile team={team} />
                    </div>
                )}
                {selectedTab === 'privacy' && (
                    <div
                        role="tabpanel"
                        id="privacy-panel"
                        aria-labelledby="privacy-tab"
                        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                    >
                        <TeamSettingsPrivacy team={team} />
                    </div>
                )}
                {selectedTab === 'members' && (
                    <div
                        role="tabpanel"
                        id="members-panel"
                        aria-labelledby="members-tab"
                        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                    >
                        <TeamSettingsMembers team={team} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default TeamSettings 